import { CheckboxField, FormDrawerContent, FormFetchDrawer, Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { applicationService } from "../shared/api/applicationService";
import { eventService } from "../shared/api/event/eventService";
import {
	ParticipantManagementUpdateFormValues,
	ParticipantManagementUpdateResource,
} from "../shared/types/application";
import { BaseApplicationDataFields } from "./BaseApplicationDataFields";
import { useBaseApplicationFormValidate } from "./baseApplicationFormValidate";

interface Props {
	open: boolean;
	onClose: () => void;
	entityId: Id;
	eventId?: Id;
}

export const EditParticipantManagementApplicationDrawer: FC<Props> = ({ entityId, eventId, ...props }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const validate = useBaseApplicationFormValidate();

	const updateParticipantManagement = useCallback(
		async (values: ParticipantManagementUpdateFormValues) => {
			const response = await applicationService.updateParticipantManagement(entityId, {
				eventId,
				deletionDate: values.deletionDate,
				setupDate: values.setupDate,
				projectNumber: values.projectNumber,
				subdomain: values.subdomain,
				configuration: values.configuration,
			});

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: eventService.indexKey });
				await queryClient.invalidateQueries({ queryKey: applicationService.keys.index });
				showToast({
					type: "success",
					title: t("Application"),
					body: t("Application was successfully updated."),
				});
				return true;
			}

			return false;
		},
		[entityId, eventId, queryClient, showToast, t],
	);

	return (
		<FormFetchDrawer
			{...props}
			title={t("Edit PARTICIPANT_MANAGEMENT")}
			validate={validate}
			onSubmit={(values) => updateParticipantManagement(values as ParticipantManagementUpdateResource)}
			queryKey={applicationService.keys.details(entityId)}
			queryFn={() => applicationService.show(entityId).then((res) => res.data.data)}
			renderSuccess={(props) => (
				<FormDrawerContent {...props}>
					<BaseApplicationDataFields />
					<CheckboxField label={t("Enable check in app")} name="configuration.enableCheckInApp" />
				</FormDrawerContent>
			)}
		/>
	);
};
